/* eslint-disable react-hooks/exhaustive-deps */
import classes from '../Combobox/Combobox.module.scss'
import natsort from 'natsort'
import {
  DropdownButton,
  InvisiButton
} from '../..'
import { FiX } from 'react-icons/fi'
import { HiCheck } from 'react-icons/hi'
import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useSelect } from 'downshift'
import type {
  ButtonShape,
  DropdownPlacement,
  GroupedOptions,
  OptionType,
  Options
} from '@eggplant/types'
import type { CSSProperties, ReactNode } from 'react'
import type { GetItemPropsOptions } from 'downshift'

export interface Props {
  customValidity?: string
  disableSorting?: boolean
  isHighlighted: boolean
  groupedOptions?: GroupedOptions
  Icon?: ({ size }: {size: string}) => JSX.Element
  initialGroupedOptions?: GroupedOptions
  options?: Options
  initialOptions?: Options
  isRequired?: boolean
  onChange: (value: string) => void
  value?: string
  isDisabled?: boolean
  noOptionsPlaceholder?: ReactNode
  prompt?: string
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  isInvalid?: boolean
  ariaLabel?: string
  isClearable?: boolean
  dropdownPlacement?: DropdownPlacement
}

const itemToString = (option?: OptionType | null) => option
  ? option.label
  : ''

// eslint-disable-next-line complexity
const ComboboxSingleSelect = ({
  ariaLabel,
  customValidity = 'Please select an item in the list.',
  disableSorting = false,
  Icon,
  isHighlighted,
  // groupedOptions = [],
  options = [],
  onChange,
  initialOptions,
  // initialGroupedOptions,
  isRequired = false,
  value = '',
  isDisabled = false,
  isInvalid = false,
  noOptionsPlaceholder,
  prompt = 'Please select an option',
  isFilter = false,
  isFilterFixedMinMaxWidth = true,
  isClearable = true,
  dropdownPlacement = 'bottom'
}: Props) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const comboBoxRef = useRef<HTMLSpanElement>(null)

  const sorter = natsort({ insensitive: true })

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedOptions = options.slice().sort((current, next) =>
    sorter(current.label, next.label)
  )

  const handleClear = () => {
    onChange('')
  }

  const renderClearButton = (
    <div className={classes.clearSection}>
      <InvisiButton
        hasHoverEffect
        label='Clear'
        onClick={handleClear}
      >
        <div className={classes.clearWrapper}>
          <FiX
            aria-hidden
            size={16}
          />
          Clear
        </div>
      </InvisiButton>
    </div>
  )

  const isSelectedClass = (item: OptionType) => item.value === value
    ? ' ' + classes.isSelected
    : ''

  const renderOptionLabel = (item: OptionType) =>
    <div className={classes.checkboxStyle}>
      <div className={classes.textStyle}>
        {item.label}
      </div>
      {item.value === value &&
        <HiCheck
          aria-hidden
          className={classes.CheckIcon}
          size={16}
        />}
    </div>

  const renderOptions = (getItemProps: (options: GetItemPropsOptions<OptionType>) => OptionType, highlightedIndex: number) => {
    const optionsList = disableSorting
      ? options
      : sortedOptions

    const combinedOptions = initialOptions
      ? [...initialOptions, ...optionsList]
      : optionsList

    return combinedOptions.length
      ? combinedOptions.map((item, index) =>
        <li
          key={item.value}
          {...getItemProps({ item, index })}
          className={
            `${isSelectedClass(item)} 
            ${highlightedIndex === index && classes.highlighted} 
            ${index === 0 && disableSorting
              ? classes.dividerStyle
              : ''
          }`
          }
          title={item.label}
        >
          {renderOptionLabel(item)}
        </li>
      )
      : null
  }

  // const renderGroupedOptions = (getItemProps: ({ item }: any) => OptionType) => {
  //   const sorter = natsort({ insensitive: true })

  //   const sortedOptions = groupedOptions.map(group => (
  //     {
  //       ...group,
  //       // eslint-disable-next-line fp/no-mutating-methods
  //       options: group.options.sort((current, next) =>
  //         sorter(current.label, next.label)
  //       )
  //     }
  //   ))

  //   const combinedOptions = initialGroupedOptions
  //     ? [...initialGroupedOptions, ...sortedOptions]
  //     : sortedOptions

  //   return combinedOptions.map(group =>
  //     <>
  //       <strong style={groupStyle}>
  //         {group.label}
  //       </strong>
  //       {
  //         group.options.map(item =>
  //           <li
  //             key={item.value}
  //             {...getItemProps({ item })}
  //             style={listStyle}
  //             title={item.label}
  //           >
  //             {item.label}
  //           </li>
  //         )
  //       }
  //     </>
  //   )
  // }

  const inputRef = useRef<HTMLInputElement>(null)

  const getComboboxStyle = () => {
    const enabledStyle = !isDisabled && !isInvalid
      ? classes.Enabled
      : ''

    const hasControlledWidthStyle = isFilter && isFilterFixedMinMaxWidth
      ? classes.FilterComboboxMinMaxWidth
      : ''

    const comboBoxClasses = [
      classes.Combobox,
      enabledStyle,
      hasControlledWidthStyle
    ].filter(className => className)

    return comboBoxClasses.join(' ')
  }

  const {
    isOpen,
    selectedItem,
    selectItem,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex
  } = useSelect({
    items: disableSorting
      ? options
      : sortedOptions,
    itemToString
  })

  const getRenderButton = () => {
    const currentOption = options.find(item =>
      item.value === value
    )

    const filterTooltip = isFilter
      ? 'Status: ' + currentOption?.label
      : currentOption?.label

    const title = currentOption?.value
      ? filterTooltip
      : prompt

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={Icon}
        dropdownPlacement={dropdownPlacement}
        isDisabled={isDisabled}
        isFilter={isFilter}
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isFocus={isOpen}
        isHighlighted={value !== '' && isHighlighted}
        isInvalid={isInvalid}
        label={
          currentOption
            ? currentOption.label
            : prompt
        }
        title={title}
      >
        {
          currentOption
            ? currentOption.label
            : prompt
        }
      </DropdownButton>
  }

  const validate = () => {
    const ref = inputRef.current

    if (!ref) return

    // reset CustomValidity when value changed
    ref.setCustomValidity('')

    if (value === selectedItem?.value) return

    if (value) {
      const selectedOption = options.find(option => option.value === value)

      if (selectedOption) selectItem(selectedOption)
    }
  }

  useEffect(validate, [value])

  useEffect(() => {
    if (value === selectedItem?.value) return

    if (selectedItem) onChange(selectedItem.value)
  }, [selectedItem])

  const dropdownWrapperClass = !isOpen
    ? classes.dropdownWrapper + ' ' + classes.Hidden
    : classes.dropdownWrapper

  const handleResize = () => {
    setWindowWidth(window.innerWidth)
  }

  window.addEventListener('resize', handleResize)

  const getComboBoxLeftPositionStyle = (comboBoxRef: React.RefObject<HTMLSpanElement>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { left: '0px' }
    }

    const comboxBoxPos = comboBoxRef.current.getBoundingClientRect().left + 415
    const adjustedLeft = 400 - comboBoxRef.current.clientWidth // fixed dropdown width = 400

    if (comboxBoxPos > windowWidth) {
      return { left: `-${adjustedLeft}px` }
    }

    return { left: '0px' }
  }

  const getComboBoxRightPositionStyle = (comboBoxRef: React.RefObject<HTMLSpanElement>): CSSProperties => {
    if (!comboBoxRef.current) {
      return { top: '-2px', left: '115px' }
    }

    const comboxBoxPos = comboBoxRef.current.offsetWidth

    return { top: '-2px', left: `${comboxBoxPos + 5}px` }
  }

  return (
    <span
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      className={getComboboxStyle()}
      ref={comboBoxRef}
      role={undefined}
    >
      <input
        checked={!!value}
        onInvalid={(event: React.ChangeEvent<HTMLInputElement>) => event.target.setCustomValidity(customValidity)}
        readOnly
        ref={inputRef}
        required={isRequired}
        type='checkbox'
      />
      {
        getRenderButton()(getToggleButtonProps())
      }
      <span
        className={dropdownWrapperClass}
        style={
          dropdownPlacement === 'bottom'
            ? getComboBoxLeftPositionStyle(comboBoxRef)
            : getComboBoxRightPositionStyle(comboBoxRef)
        }
      >
        {
          options.length // || groupedOptions.length
            ? (
              <>
                <ul {...getMenuProps({}, { suppressRefError: true })}>
                  {
                    isOpen && !isDisabled &&
                      <>
                        {
                          !!options.length && renderOptions(getItemProps, highlightedIndex)
                        }
                        {/* {
                        !!groupedOptions.length && renderGroupedOptions(getItemProps)
                      } */}
                      </>
                  }
                </ul>
                {
                  (value && isClearable) && renderClearButton
                }
              </>
            )
            : noOptionsPlaceholder
        }
      </span>
    </span>
  )
}

export default ComboboxSingleSelect
